import React, { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { AppDispatch } from "redux/store";
import { similar_Properties } from "../../../../redux/reducers/Properties/PropertiesSlice";
import { PropertyToEdit } from "redux/reducers/Properties/PropertiesSliceTypes";
import StayCardSkeleton from "shared/Skelton/CardSkeltion";
import SimilarCard from "components/StayCard/SimilarCard";

interface SimilarPropertyProps {
  propertyData: PropertyToEdit | null;
}

const SimilarProperty: React.FC<SimilarPropertyProps> = ({ propertyData }) => {
  const dispatch = useDispatch<AppDispatch>();
  const [similarProperties, setSimilarProperties] = useState<any[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const property_type_id = propertyData?.property_type?.id;
  const property_category_id = propertyData?.property_category?.id;
  const property_id = propertyData?.id;

  const fetchSimilarProperties = async (): Promise<void> => {
    setLoading(true);
    setError(null);
    try {
      const response = await dispatch(
        similar_Properties({
          property_type_id: property_type_id ?? 0,
          property_category_id: property_category_id ?? 0,
          property_id: property_id ?? 0,
        })
      ).unwrap();
      setSimilarProperties(response?.properties || []);
    } catch (err: any) {
      console.error("Error fetching similar properties:", err);
      setError(err.message || "Failed to fetch similar properties");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (
      propertyData?.property_type?.id &&
      propertyData?.property_category?.id &&
      propertyData?.id
    ) {
      fetchSimilarProperties();
    }
  }, [propertyData?.property_type?.id, propertyData?.property_category?.id, propertyData?.id]);

  const handleRetry = (): void => {
    fetchSimilarProperties();
  };

  return (
    <div className="listingSection__wrap w-full">
      {/* Section Header */}
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-neutral-900 dark:text-neutral-100">
          Similar Properties
        </h2>
        <p className="mt-1 text-sm text-neutral-600 dark:text-neutral-400">
          Discover more properties like this one
        </p>
      </div>

      {/* Loading State */}
      {loading && (
        <div className="grid gap-6 grid-cols-1 sm:grid-cols-2 lg:grid-cols-3">
          <StayCardSkeleton />
          <StayCardSkeleton />
          <StayCardSkeleton />
        </div>
      )}

      {/* Error State */}
      {!loading && error && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800/30 rounded-xl p-6 text-center">
          <div className="max-w-sm mx-auto">
            <div className="w-12 h-12 mx-auto mb-3 bg-red-100 dark:bg-red-900/30 rounded-full flex items-center justify-center">
              <svg className="w-6 h-6 text-red-600 dark:text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
            </div>
            <h3 className="text-base font-semibold text-red-900 dark:text-red-100 mb-1">
              Unable to Load
            </h3>
            <p className="text-sm text-red-700 dark:text-red-300 mb-4">
              {error}
            </p>
            <button
              onClick={handleRetry}
              className="px-5 py-2 bg-red-600 hover:bg-red-700 text-white text-sm font-semibold rounded-lg transition-colors"
            >
              Try Again
            </button>
          </div>
        </div>
      )}

      {/* Empty State */}
      {!loading && !error && similarProperties.length === 0 && (
        <div className="bg-white dark:bg-neutral-800 border border-neutral-200 dark:border-neutral-700 rounded-xl p-8 text-center">
          <div className="max-w-sm mx-auto">
            <div className="w-16 h-16 mx-auto mb-4 bg-neutral-100 dark:bg-neutral-800 rounded-full flex items-center justify-center">
              <svg className="w-8 h-8 text-neutral-400 dark:text-neutral-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
              </svg>
            </div>
            <h3 className="text-lg font-bold text-neutral-900 dark:text-neutral-100 mb-2">
              No Similar Properties
            </h3>
            <p className="text-sm text-neutral-600 dark:text-neutral-400">
              We couldn't find any similar properties at the moment.
            </p>
          </div>
        </div>
      )}

      {/* Properties Grid */}
      {!loading && !error && similarProperties.length > 0 && (
        <>
          <div className="grid gap-6 grid-cols-1 sm:grid-cols-2 lg:grid-cols-3">
            {similarProperties.map((property, index) => (
              <div key={property.id || index} className="transition-transform hover:-translate-y-1">
                <SimilarCard similarProperties={property} />
              </div>
            ))}
          </div>
          
          {/* Property Count */}
          <div className="mt-6 text-center">
            <p className="text-sm text-neutral-500 dark:text-neutral-400">
              {similarProperties.length} similar {similarProperties.length === 1 ? "property" : "properties"}
            </p>
          </div>
        </>
      )}
    </div>
  );
};

export default SimilarProperty;